import React, { useEffect } from "react";
import "./App.css";

import {
  Card,
  WixDesignSystemProvider,
  WixDesignSystemIconThemeProvider,
  WixDesignSystemDefaultPropsProvider,
  type WixDesignSystemDefaultProps,
  Button,
  MarketingLayout,
  Image,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import "@wix/design-system/themes/odeditor.global.css";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import { IconThemeProvider } from "@wix/wix-ui-icons-common/core";
import { AIFilledSmall } from "@wix/wix-ui-icons-common/lazy";
import SupportAvatar from "./support-avatar.png";

type SupportedLocale =
  | "en"
  | "es"
  | "pt"
  | "fr"
  | "de"
  | "pl"
  | "it"
  | "ru"
  | "ja"
  | "ko"
  | "tr"
  | "zh"
  | "th"
  | "sv"
  | "no"
  | "nl"
  | "da"
  | "cs"
  | "id"
  | "vi"
  | "uk";

type Messages = {
  title: string;
  description: string;
  cta: string;
};

const messages: Record<SupportedLocale, Messages> = {
  en: {
    title: "We are here to help",
    description:
      "Your dedicated team from Certified Code is here to help you with any questions you may have.",
    cta: "Chat with us",
  },
  es: {
    title: "Estamos aquí para ayudar",
    description:
      "Tu equipo dedicado de Certified Code está aquí para ayudarte con cualquier pregunta que tengas.",
    cta: "Chatea con nosotros",
  },
  pt: {
    title: "Estamos aqui para ajudar",
    description:
      "Sua equipe dedicada da Certified Code está aqui para ajudar com qualquer dúvida que você tenha.",
    cta: "Fale conosco",
  },
  fr: {
    title: "Nous sommes là pour vous aider",
    description:
      "Votre équipe dédiée de Certified Code est là pour répondre à toutes vos questions.",
    cta: "Discutez avec nous",
  },
  de: {
    title: "Wir sind für Sie da",
    description:
      "Ihr persönliches Team von Certified Code hilft Ihnen gerne bei allen Fragen.",
    cta: "Mit uns chatten",
  },
  pl: {
    title: "Jesteśmy tutaj, aby pomóc",
    description:
      "Dedykowany zespół Certified Code jest tutaj, aby pomóc Ci z wszelkimi pytaniami.",
    cta: "Porozmawiaj z nami",
  },
  it: {
    title: "Siamo qui per aiutarti",
    description:
      "Il tuo team dedicato di Certified Code è qui per aiutarti con qualsiasi domanda.",
    cta: "Chatta con noi",
  },
  ru: {
    title: "Мы здесь, чтобы помочь",
    description:
      "Ваша выделенная команда Certified Code готова помочь вам с любыми вопросами.",
    cta: "Напишите нам",
  },
  ja: {
    title: "サポートいたします",
    description:
      "Certified Code の専任チームが、あらゆるご質問にお答えします。",
    cta: "チャットする",
  },
  ko: {
    title: "도와드리겠습니다",
    description:
      "Certified Code 전담 팀이 궁금한 점을 언제든지 도와드립니다.",
    cta: "채팅하기",
  },
  tr: {
    title: "Yardım etmek için buradayız",
    description:
      "Certified Code'daki özel ekibiniz, tüm sorularınızda size yardımcı olmak için burada.",
    cta: "Bizimle sohbet edin",
  },
  zh: {
    title: "我们随时为您提供帮助",
    description:
      "Certified Code 的专属团队随时为您解答任何问题。",
    cta: "与我们聊天",
  },
  th: {
    title: "เราพร้อมช่วยเหลือคุณ",
    description:
      "ทีมงานเฉพาะของคุณจาก Certified Code พร้อมช่วยตอบทุกคำถามของคุณ",
    cta: "แชทกับเรา",
  },
  sv: {
    title: "Vi finns här för att hjälpa",
    description:
      "Ditt dedikerade team från Certified Code hjälper dig gärna med alla frågor du kan ha.",
    cta: "Chatta med oss",
  },
  no: {
    title: "Vi er her for å hjelpe",
    description:
      "Ditt dedikerte team fra Certified Code er her for å hjelpe deg med spørsmål du måtte ha.",
    cta: "Chat med oss",
  },
  nl: {
    title: "We zijn er om te helpen",
    description:
      "Je toegewijde team van Certified Code staat klaar om je te helpen met al je vragen.",
    cta: "Chat met ons",
  },
  da: {
    title: "Vi er her for at hjælpe",
    description:
      "Dit dedikerede team fra Certified Code er her for at hjælpe dig med alle spørgsmål.",
    cta: "Chat med os",
  },
  cs: {
    title: "Jsme tu, abychom pomohli",
    description:
      "Váš specializovaný tým z Certified Code vám rád pomůže s jakýmikoli dotazy.",
    cta: "Chatujte s námi",
  },
  id: {
    title: "Kami siap membantu",
    description:
      "Tim khusus Anda dari Certified Code siap membantu menjawab pertanyaan apa pun.",
    cta: "Chat dengan kami",
  },
  vi: {
    title: "Chúng tôi luôn sẵn sàng hỗ trợ",
    description:
      "Đội ngũ riêng của bạn từ Certified Code luôn sẵn sàng hỗ trợ mọi câu hỏi.",
    cta: "Trò chuyện với chúng tôi",
  },
  uk: {
    title: "Ми тут, щоб допомогти",
    description:
      "Ваша спеціальна команда Certified Code готова допомогти з будь-якими запитаннями.",
    cta: "Напишіть нам",
  },
};

const localeAliases: Record<string, SupportedLocale> = {
  cn: "zh",
  zh: "zh",
  "zh-cn": "zh",
  "zh-hans": "zh",
  "zh-sg": "zh",
  "zh-tw": "zh",
  "zh-hant": "zh",
  "pt-br": "pt",
  "pt-pt": "pt",
  nb: "no",
  nn: "no",
  no: "no",
  in: "id",
  id: "id",
  ua: "uk",
  uk: "uk",
};

const hiddenInstanceIds = new Set([
  "f85651b2-6d16-4a32-b92c-33d42f69c801",
  "04e5d456-edeb-4671-8fd6-ad4d88a1d179",
]);

const BUSINESS_MANAGER_DEFAULTS: WixDesignSystemDefaultProps = {
  Button: { size: "small" as const, skin: "dark" as const },
  IconButton: {
    size: "small" as const,
    skin: "dark" as const,
    priority: "tertiary" as const,
  },
  TextButton: { size: "small" as const, skin: "standard" as const },
  Input: { size: "small" as const },
};

function getLocaleFromQuery(search: string): SupportedLocale {
  const lang = new URLSearchParams(search).get("lang");
  if (!lang) {
    return "en";
  }

  const normalized = lang.toLowerCase().replace("_", "-");
  const baseLocale = normalized.split("-")[0];

  return (
    localeAliases[normalized] ||
    localeAliases[baseLocale] ||
    (baseLocale in messages ? (baseLocale as SupportedLocale) : "en")
  );
}

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function App() {
  const locale = getLocaleFromQuery(window.location.search);
  const copy = messages[locale];

  useEffect(() => {
    try {
      if (inIframe()) {
        createClient({
          host: dashboard.host(),
          auth: dashboard.auth(),
          modules: {
            dashboard,
          },
        });
      }
    } catch (error) {
      throw error;
    }

    try {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.style.display = "none";
      }

      const instanceToken = new URLSearchParams(window.location.search).get(
        "instance"
      );
      if (!instanceToken) {
        // Allow the standalone local development server to render without a Wix instance token.
        if (process.env.NODE_ENV === "development") {
          showApp();
        }
        return;
      }

      fetch("https://www.wixapis.com/oauth2/token-info", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: instanceToken }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data?.instanceId && !hiddenInstanceIds.has(data.instanceId)) {
            showApp();
          }
        });
    } catch (error) {
      console.error(error);
    }

    function showApp() {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.style.display = "block";
      }
    }
  }, []);

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <WixDesignSystemIconThemeProvider>
        <IconThemeProvider theme="odeditor">
          <WixDesignSystemDefaultPropsProvider defaults={BUSINESS_MANAGER_DEFAULTS}>
            <Card>
              <MarketingLayout
                size="tiny"
                title={copy.title}
                description={copy.description}
                actions={
                  <Button
                    suffixIcon={<AIFilledSmall />}
                    as="a"
                    href="https://support.certifiedcode.us/?utm_source=wix"
                    target="_blank"
                  >
                    {copy.cta}
                  </Button>
                }
                image={<Image src={SupportAvatar} width={100} transparent />}
              />
            </Card>
          </WixDesignSystemDefaultPropsProvider>
        </IconThemeProvider>
      </WixDesignSystemIconThemeProvider>
    </WixDesignSystemProvider>
  );
}

export default App;
